import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
type StartQuestionTransition = (updater: () => void) => void;
type ViewTransition = {
  finished: Promise<void>;
};
export type DecisionToolTransitionState = {
  startQuestionTransition: StartQuestionTransition;
  isDelayElapsed: boolean;
};
const updateWithViewTransition = (updater: () => void): ViewTransition | null => {
  if (!('startViewTransition' in document)) {
    flushSync(updater);
    return null;
  }
  return document.startViewTransition(() => {
    flushSync(updater);
  });
};

// Check if the title is completely in the viewport, even horinzontally.
const isTitleFullyVisible = (element: HTMLElement): boolean => {
  // The height of the menu-bar
  const scrollPaddingBlockStart = getComputedStyle(document.documentElement).getPropertyValue('scroll-padding-block-start');
  const rect = element.getBoundingClientRect();
  return rect.top >= parseFloat(scrollPaddingBlockStart) && rect.left >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && rect.right <= (window.innerWidth || document.documentElement.clientWidth);
};
const scrollToQuestionOrResult = (currentQuestionElId: string) => {
  requestAnimationFrame(() => {
    const currentQuestionContainer = document.getElementById(`${currentQuestionElId}-container`);
    const currentQuestionTitle = document.getElementById(`${currentQuestionElId}-title`);
    if (currentQuestionTitle != null && currentQuestionContainer != null) {
      if (!isTitleFullyVisible(currentQuestionTitle)) {
        currentQuestionContainer.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
      currentQuestionTitle.focus({
        preventScroll: true
      });
      return;
    }
    const resultsTitle = document.getElementById('decision-tool-results-title');
    // No current question. If there are results showing; scroll to that
    if (resultsTitle != null) {
      if (!isTitleFullyVisible(resultsTitle)) {
        resultsTitle.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
      resultsTitle.focus({
        preventScroll: true
      });
    }
  });
};

// Hook to manage the transition state when moving between questions in the decision tool.
// It contains a timeout that will toggle the `isDelayElapsed` state after a short delay.
// When the delay elapses, this hook will scroll to the current question or (if no current question) the results via `scrollToQuestionOrResult`.
export const useStartQuestionTransition = currentQuestionElId => {
  const $ = _c(7);
  const [isDelayElapsed, setIsDelayElapsed] = useState(true);
  const pendingChangeRef = useRef(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => () => {
      if (pendingChangeRef.current != null) {
        window.clearTimeout(pendingChangeRef.current);
      }
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== currentQuestionElId) {
    const queueDelayElapsed = () => {
      if (pendingChangeRef.current != null) {
        window.clearTimeout(pendingChangeRef.current);
      }
      pendingChangeRef.current = setTimeout(() => {
        const transition = updateWithViewTransition(() => {
          setIsDelayElapsed(true);
        });
        if (transition == null) {
          scrollToQuestionOrResult(currentQuestionElId);
        } else {
          transition.finished.finally(() => {
            scrollToQuestionOrResult(currentQuestionElId);
          });
        }
        pendingChangeRef.current = null;
      }, 500);
    };
    t2 = updater => {
      if (pendingChangeRef.current != null) {
        window.clearTimeout(pendingChangeRef.current);
      }
      updateWithViewTransition(() => {
        setIsDelayElapsed(false);
        queueDelayElapsed();
        updater();
      });
    };
    $[2] = currentQuestionElId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const startQuestionTransition = t2;
  let t3;
  if ($[4] !== isDelayElapsed || $[5] !== startQuestionTransition) {
    t3 = {
      startQuestionTransition,
      isDelayElapsed
    };
    $[4] = isDelayElapsed;
    $[5] = startQuestionTransition;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};